import { motion } from 'framer-motion';
import { currency, type CurrencyCode } from '@/shared/lib/format';
import { quantityLabel } from '@/shared/lib/passbook-utils';
import { Spinner } from '@/shared/ui/spinner';
import type { Passbook } from '@/shared/types';
import type { OperatorPreviewResult } from '../api/operator-preview';

interface OperatorSummaryCardProps {
  passbook: Passbook;
  preview: OperatorPreviewResult | null;
  previewLoading?: boolean;
  /** Falha do simulate / regra — ex.: "Operação fora da regra". */
  previewError?: string | null;
  redemptionExceeded?: boolean;
  currencyCode: CurrencyCode;
  /** Nome do produto resgatado (catálogo). */
  redemptionProductName?: string | null;
  redemptionOn?: boolean;
}

const enterTransition = {
  duration: 0.32,
  ease: [0.22, 1, 0.36, 1] as const,
};

function awardLabel(
  preview: OperatorPreviewResult,
  label: string,
  fmt: (n: number) => string,
): string {
  if (preview.program === 'cashback') return fmt(preview.cashbackEarned);
  if (preview.program === 'points') return `${preview.pointsEarned} pts`;
  return `${preview.stampsEarned || preview.awardUnits} ${label.toLowerCase()}`;
}

export function OperatorSummaryCard({
  passbook,
  preview,
  previewLoading,
  previewError,
  redemptionExceeded,
  currencyCode,
  redemptionProductName,
  redemptionOn,
}: OperatorSummaryCardProps) {
  const label = quantityLabel(passbook);
  const fmt = (n: number) => currency(n, currencyCode);
  const redemptionLine =
    redemptionOn && redemptionProductName ? `Produto: ${redemptionProductName}` : null;

  const loading = previewLoading && !preview && !previewError;
  const redemptionShort = redemptionLine
    ? redemptionLine.replace(/^Produto:\s*/i, '')
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={enterTransition}
      className="order-first flex w-full shrink-0 flex-col lg:order-none lg:h-full lg:w-[18rem]"
    >
      {/* Mobile: faixa compacta — Compra / Total / Resgate sempre à vista */}
      <aside
        className="glass glass-sheen flex w-full flex-col gap-1 rounded-2xl px-3 py-2 lg:hidden"
        aria-label="Resumo da venda"
      >
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-1 text-sm text-muted-foreground">
            <Spinner className="size-4" />
            Calculando…
          </div>
        ) : previewError ? (
          <p className="py-0.5 text-center text-xs font-medium text-destructive">{previewError}</p>
        ) : preview ? (
          <>
            <div className="grid grid-cols-3 items-end gap-2">
              <div className="min-w-0">
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Compra
                </p>
                <p className="truncate text-sm font-semibold tabular-nums">
                  {fmt(preview.purchaseValue)}
                </p>
              </div>
              <div className="min-w-0 text-center">
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Total
                </p>
                <p className="truncate text-xl font-bold tabular-nums tracking-tight text-primary">
                  {fmt(preview.netValue)}
                </p>
              </div>
              <div className="min-w-0 text-right">
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {redemptionShort ? 'Resgate' : preview.awardUnits > 0 ? 'Ganha' : 'Resgate'}
                </p>
                <p
                  className={`truncate text-sm font-semibold tabular-nums ${
                    redemptionExceeded ? 'text-destructive' : ''
                  }`}
                >
                  {redemptionShort
                    ? redemptionShort
                    : preview.awardUnits > 0
                      ? awardLabel(preview, label, fmt)
                      : preview.discount > 0
                        ? `−${fmt(preview.discount)}`
                        : '—'}
                </p>
              </div>
            </div>
            {redemptionExceeded ? (
              <p className="text-center text-[11px] font-medium text-destructive">
                Ajuste o resgate — acima do disponível
              </p>
            ) : null}
          </>
        ) : (
          <p className="py-0.5 text-center text-xs text-muted-foreground">
            Informe o valor da compra para ver o cálculo
          </p>
        )}
      </aside>

      {/* Desktop: card lateral completo */}
      <aside className="glass glass-sheen hidden min-h-0 flex-1 flex-col overflow-hidden rounded-[1.5rem] p-4 lg:flex">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">Resumo</p>
        <h3 className="mt-0.5 text-base font-semibold tracking-tight">Conta da venda</h3>

        <div className="mt-4 min-h-0 flex-1 space-y-2.5 overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="size-4" />
              Calculando…
            </div>
          ) : previewError ? (
            <p className="text-sm font-medium text-destructive">{previewError}</p>
          ) : preview ? (
            <>
              <div className="flex justify-between gap-2 text-sm">
                <span className="text-muted-foreground">Compra</span>
                <span className="tabular-nums">{fmt(preview.purchaseValue)}</span>
              </div>
              {preview.discount > 0 ? (
                <div className="flex justify-between gap-2 text-sm">
                  <span className="text-muted-foreground">Desconto</span>
                  <span
                    className={`tabular-nums ${
                      redemptionExceeded
                        ? 'text-destructive'
                        : 'text-emerald-600 dark:text-emerald-400'
                    }`}
                  >
                    −{fmt(preview.discount)}
                  </span>
                </div>
              ) : null}
              <div className="flex items-end justify-between gap-2 border-t border-dashed border-[hsl(var(--glass-border)/0.5)] pt-2.5">
                <span className="text-sm font-semibold">Total</span>
                <span className="text-2xl font-bold tabular-nums tracking-tight text-primary">
                  {fmt(preview.netValue)}
                </span>
              </div>
              {redemptionLine ? (
                <div className="flex justify-between gap-2 text-sm">
                  <span className="text-muted-foreground">Resgate</span>
                  <span className="truncate text-right font-medium">{redemptionLine}</span>
                </div>
              ) : null}
              {preview.awardUnits > 0 ? (
                <div className="glass-thin mt-1 rounded-xl px-3 py-2.5 text-center">
                  <p className="text-xs text-muted-foreground">Cliente ganha</p>
                  <p className="mt-0.5 text-lg font-bold tabular-nums text-primary">
                    {awardLabel(preview, label, fmt)}
                  </p>
                </div>
              ) : null}
              {redemptionExceeded ? (
                <p className="text-sm font-medium text-destructive">
                  Ajuste o resgate — valor acima do disponível.
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Informe o valor da compra para ver o cálculo.
            </p>
          )}
        </div>

        <div className="mt-3 flex shrink-0 items-center justify-between gap-2 border-t border-dashed border-[hsl(var(--glass-border)/0.45)] pt-2 text-[10px] uppercase tracking-wide text-muted-foreground">
          <span>Fixe CRM</span>
          <span>venda em andamento</span>
        </div>
      </aside>
    </motion.div>
  );
}
